// DM-7: single undo/redo stack of Commands. Redo cleared on new dispatch.
// Coalescing happens in tools (one command per gesture) — never post-hoc here.
import type { Doc } from "./entities";
import { applyCommand, invertCommand, type Command } from "./commands";

/** Listener receives the command that just mutated the doc (the inverse, for undo) or none on bulk reload. */
export type StoreListener = (doc: Doc, cmd?: Command) => void;

export class DocStore {
  doc: Doc;
  private undoStack: Command[] = [];
  private redoStack: Command[] = [];
  private listeners = new Set<StoreListener>();
  /**
   * Follow-up work after every dispatched command (pipes, 2026-10-06: rebuild pipes whose ends
   * moved). The command and its follow-up are recorded as ONE undo step. A deriver that throws
   * never loses the command: it is kept on its own and a warning is logged.
   */
  deriver?: (doc: Doc, cmd: Command) => Command | null;

  constructor(doc: Doc) {
    this.doc = doc;
  }

  subscribe(fn: StoreListener): () => void {
    this.listeners.add(fn);
    return () => this.listeners.delete(fn);
  }

  private notify(cmd?: Command): void {
    for (const fn of this.listeners) fn(this.doc, cmd);
  }

  dispatch(cmd: Command): void {
    applyCommand(this.doc, cmd);
    let recorded = cmd;
    if (this.deriver) {
      try {
        const followUp = this.deriver(this.doc, cmd);
        if (followUp) {
          applyCommand(this.doc, followUp);
          recorded = { kind: "batch", label: commandLabel(cmd), commands: [cmd, followUp] };
        }
      } catch (err) {
        console.warn("store: follow-up after a command failed; the command is kept", err);
      }
    }
    this.undoStack.push(recorded);
    this.redoStack = [];
    this.notify(recorded);
  }

  get canUndo(): boolean {
    return this.undoStack.length > 0;
  }

  get canRedo(): boolean {
    return this.redoStack.length > 0;
  }

  /** Human label of what Undo would revert ("add polyline", "delete 3 objects") or null. */
  undoLabel(): string | null {
    const cmd = this.undoStack[this.undoStack.length - 1];
    return cmd ? commandLabel(cmd) : null;
  }

  redoLabel(): string | null {
    const cmd = this.redoStack[this.redoStack.length - 1];
    return cmd ? commandLabel(cmd) : null;
  }

  undo(): void {
    const cmd = this.undoStack.pop();
    if (!cmd) return;
    const inverse = invertCommand(cmd);
    applyCommand(this.doc, inverse);
    this.redoStack.push(cmd);
    this.notify(inverse);
  }

  redo(): void {
    const cmd = this.redoStack.pop();
    if (!cmd) return;
    applyCommand(this.doc, cmd);
    this.undoStack.push(cmd);
    this.notify(cmd);
  }

  /** PST-4: persisted history tail (last 50 + position). */
  historyTail(): { undo: Command[]; redo: Command[] } {
    return { undo: this.undoStack.slice(-50), redo: this.redoStack.slice(-50) };
  }

  restoreHistory(tail: { undo: Command[]; redo: Command[] }): void {
    this.undoStack = tail.undo;
    this.redoStack = tail.redo;
  }

  /** PST-3: swap in a loaded document (open project / import). Cmd-less notify → full rebuilds. */
  replace(doc: Doc, tail?: { undo: Command[]; redo: Command[] }): void {
    this.doc = doc;
    this.undoStack = tail?.undo ?? [];
    this.redoStack = tail?.redo ?? [];
    this.notify();
  }
}

function commandLabel(cmd: Command): string {
  switch (cmd.kind) {
    case "addEntities":
      return cmd.entities.length === 1 ? `add ${cmd.entities[0]!.type}` : `add ${cmd.entities.length} objects`;
    case "deleteEntities":
      return cmd.removed.length === 1 ? "delete" : `delete ${cmd.removed.length} objects`;
    case "updateEntities":
      return cmd.patches.length === 1 ? "edit" : `edit ${cmd.patches.length} objects`;
    case "reorder":
      return "reorder";
    case "addLayer":
      return "add layer";
    case "deleteLayer":
      return "delete layer";
    case "updateLayer":
      return "layer change";
    case "reorderLayer":
      return "reorder layers";
    case "group":
      return "group";
    case "ungroup":
      return "ungroup";
    case "setDocProps":
      return "document settings";
    case "addAsset":
    case "removeAsset":
      return "image asset";
    case "batch":
      return cmd.label;
  }
}
